import { useStyletron } from "baseui";
import {
  DatePicker as BaseDatePicker,
  DatepickerProps,
} from "baseui/datepicker";
import pl from "date-fns/locale/pl";
import React from "react";
import { Controller, UseControllerProps } from "react-hook-form";
import { StyleObject } from "styletron-react";

type Props = {
  $style?: StyleObject;
  isDisabled?: boolean;
  date: Date | null;
  setDate: (date: Date) => void;
} & DatepickerProps;

export default function DatePicker({
  $style,
  isDisabled,
  date,
  setDate,
  ...props
}: Props): React.ReactElement {
  const [, theme] = useStyletron();

  return (
    <BaseDatePicker
      value={date}
      onChange={({ date }) => setDate(date as Date)}
      locale={pl}
      formatString="dd.MM.yyyy"
      placeholder="DD.MM.RRRR"
      clearable
      overrides={{
        Popover: {
          props: {
            overrides: {
              Body: {
                style: {
                  zIndex: 300,
                },
              },
            },
          },
        },
        MonthYearSelectPopover: {
          props: {
            overrides: {
              Body: {
                style: {
                  zIndex: 301,
                },
              },
            },
          },
        },
        CalendarHeader: {
          style: {
            backgroundColor: theme.colors.primary,
          },
        },
        Input: {
          props: {
            overrides: {
              Root: {
                style: () => ({
                  borderTopLeftRadius: theme.borders.radius300,
                  borderTopRightRadius: theme.borders.radius300,
                  borderBottomRightRadius: theme.borders.radius300,
                  borderBottomLeftRadius: theme.borders.radius300,
                  ...$style,
                }),
              },
            },
            disabled: isDisabled,
          },
        },
        MonthHeader: {
          style: {
            backgroundColor: theme.colors.backgroundTertiary,
            color: "#000",
          },
        },
      }}
      {...props}
    />
  );
}

export function ControlledDatePicker({
  control,
  name,
  rules,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
UseControllerProps<any>): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value } }) => (
        <DatePicker
          setDate={(date: Date) => onChange(date)}
          date={value ? new Date(value) : null}
        />
      )}
    />
  );
}
